import { useState, useEffect } from 'react';
import { Layout, Modal, message } from 'antd';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useNotes } from '../../contexts/NotesContext.jsx';
import { useFolders } from '../../contexts/FoldersContext.jsx';
import { useTags } from '../../contexts/TagsContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import Sidebar from './Sidebar.jsx';
import NoteList from '../Notes/NoteList.jsx';
import Editor from '../Editor/Editor.jsx';
import Settings from './Settings.jsx';
import Account from './Account.jsx';
import MobileHeader from './MobileHeader.jsx';
import BottomNav from './BottomNav.jsx';
import ShareModal from '../Modals/ShareModal.jsx';
import FolderModal from '../Modals/FolderModal.jsx';
import TagModal from '../Modals/TagModal.jsx';
import CreateNoteModal from '../Modals/CreateNoteModal.jsx';
import { ResizableContainer, ResizablePanel, ResizeHandle, useResizable } from '../common/ResizablePanel.jsx';
import Icon from '../common/Icon.jsx';
import './Dashboard.css';

const { Header, Sider, Content } = Layout;

// Separate component for desktop notes view to properly use the useResizable hook
function DesktopNotesView({ selectedNote, onSelectNote, onShare, onCreateNote, isTablet }) {
    // Use the resizable hook with localStorage persistence
    // On tablet, use narrower default width for better space usage
    const { width, startResize, isResizing } = useResizable(
        'noteListWidth',  // localStorage key
        isTablet ? 280 : 350,  // default width (narrower on tablet)
        isTablet ? 250 : 280,  // min width
        isTablet ? 400 : 500   // max width
    );

    // Collapse state with localStorage persistence
    const [isNoteListCollapsed, setIsNoteListCollapsed] = useState(() => {
        const saved = localStorage.getItem('noteListCollapsed');
        return saved === 'true';
    });

    // Toggle collapse handler
    const handleToggleCollapse = () => {
        setIsNoteListCollapsed(prev => {
            const newValue = !prev;
            localStorage.setItem('noteListCollapsed', String(newValue));
            return newValue;
        });
    };

    return (
        <ResizableContainer>
            <ResizablePanel
                width={width}
                minWidth={isTablet ? 250 : 280}
                maxWidth={isTablet ? 400 : 500}
                className={`note-list-panel ${isTablet ? 'tablet-mode' : ''}`}
                collapsed={isNoteListCollapsed}
                collapsedWidth={48}
            >
                <NoteList
                    onSelectNote={onSelectNote}
                    selectedNoteId={selectedNote?.id}
                    onShare={onShare}
                    onCreateNote={onCreateNote}
                    isCollapsed={isNoteListCollapsed}
                    onToggleCollapse={handleToggleCollapse}
                />
            </ResizablePanel>
            <ResizeHandle
                onResize={startResize}
                isResizing={isResizing}
                vertical={true}
                hidden={isNoteListCollapsed}
            />
            <ResizablePanel className="editor-panel">
                {selectedNote ? (
                    <Editor note={selectedNote} onShare={() => onShare(selectedNote)} />
                ) : (
                    <div className="empty-state">
                        <Icon name="FileText" size="xl" />
                        <p>Select a note to view and edit</p>
                    </div>
                )}
            </ResizablePanel>
        </ResizableContainer>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const { selectedNote, selectNote } = useNotes();
    const { deleteFolder } = useFolders();
    const { deleteTag } = useTags();
    const { isDark } = useTheme();

    const [currentView, setCurrentView] = useState('notes');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);
    const [showEditor, setShowEditor] = useState(false);

    // Share modal state
    const [shareModalState, setShareModalState] = useState({
        isOpen: false,
        note: null
    });

    // Folder modal state
    const [folderModalState, setFolderModalState] = useState({
        isOpen: false,
        folder: null
    });

    // Tag modal state
    const [tagModalState, setTagModalState] = useState({
        isOpen: false,
        tag: null
    });

    // Create note modal state
    const [createNoteModalOpen, setCreateNoteModalOpen] = useState(false);

    const handleCreateNote = () => {
        setCreateNoteModalOpen(true);
    };

    const closeCreateNoteModal = () => {
        setCreateNoteModalOpen(false);
    };

    const handleNoteCreated = (noteKey) => {
        // Find and select the newly created note after sync
        // The note list will auto-update via subscription
    };

    const handleShare = (note) => {
        setShareModalState({
            isOpen: true,
            note: note
        });
    };

    const closeShareModal = () => {
        setShareModalState(prev => ({ ...prev, isOpen: false }));
    };

    const handleCreateFolder = () => {
        setFolderModalState({ isOpen: true, folder: null });
    };

    const closeFolderModal = () => {
        setFolderModalState({ isOpen: false, folder: null });
    };

    const handleCreateTag = () => {
        setTagModalState({ isOpen: true, tag: null });
    };

    const closeTagModal = () => {
        setTagModalState({ isOpen: false, tag: null });
    };

    // Edit handlers - open modal with existing item
    const handleEditFolder = (folder) => {
        setFolderModalState({ isOpen: true, folder });
    };

    const handleEditTag = (tag) => {
        setTagModalState({ isOpen: true, tag });
    };

    // Delete handlers with confirmation
    const handleDeleteFolder = (folder) => {
        Modal.confirm({
            title: `Delete "${folder.name}"?`,
            content: 'Notes in this folder will be moved to "All Notes".',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await deleteFolder(folder.id);
                    message.success('Folder deleted');
                } catch (err) {
                    message.error('Failed to delete folder');
                }
            }
        });
    };

    const handleDeleteTag = (tag) => {
        Modal.confirm({
            title: `Delete "${tag.name}"?`,
            content: 'This tag will be removed from all notes.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await deleteTag(tag.id);
                    message.success('Tag deleted');
                } catch (err) {
                    message.error('Failed to delete tag');
                }
            }
        });
    };

    // Detect mobile and tablet viewport - align with CSS breakpoints
    // Breakpoints: mobile ≤768px, tablet 769-1024px, desktop >1024px
    useEffect(() => {
        const checkViewport = () => {
            const width = window.innerWidth;
            // Use 768px for tablet/mobile transition (consistent with CSS)
            setIsMobile(width <= 768);
            // Tablet range: 769px - 1024px
            setIsTablet(width > 768 && width <= 1024);
        };
        checkViewport();
        window.addEventListener('resize', checkViewport);
        return () => window.removeEventListener('resize', checkViewport);
    }, []);

    // Auto-collapse sidebar on tablet for better space usage
    useEffect(() => {
        if (isTablet && !collapsed) {
            setCollapsed(true);
        }
    }, [isTablet]);

    // Show editor when note selected on mobile
    useEffect(() => {
        if (isMobile && selectedNote) {
            setShowEditor(true);
        }
    }, [selectedNote, isMobile]);

    const handleViewChange = (view) => {
        setCurrentView(view);
        setSidebarOpen(false);
    };

    const handleBackFromEditor = () => {
        setShowEditor(false);
        selectNote(null);
    };

    const getHeaderTitle = () => {
        switch (currentView) {
            case 'settings': return 'Settings';
            case 'account': return 'Account';
            default: return 'Page Notes';
        }
    };

    const handleNoteSelect = (note) => {
        selectNote(note);
        if (isMobile) {
            setShowEditor(true);
        }
    };

    const renderMainContent = () => {
        if (currentView === 'settings') {
            return <Settings />;
        }

        if (currentView === 'account') {
            return <Account />;
        }

        // Notes view - desktop/tablet with resizable panels
        if (!isMobile) {
            return (
                <DesktopNotesView
                    selectedNote={selectedNote}
                    onSelectNote={handleNoteSelect}
                    onShare={handleShare}
                    onCreateNote={handleCreateNote}
                    isTablet={isTablet}
                />
            );
        }

        // Mobile view
        if (showEditor && selectedNote) {
            return <Editor note={selectedNote} onShare={() => handleShare(selectedNote)} />;
        }

        return (
            <NoteList
                onSelectNote={handleNoteSelect}
                selectedNoteId={selectedNote?.id}
                onShare={handleShare}
                onCreateNote={handleCreateNote}
            />
        );
    };

    return (
            <Layout style={{ minHeight: '100vh' }} className={isDark ? 'theme-dark' : 'theme-light'}>
                {/* Desktop Sidebar */}
                {!isMobile && (
                    <Sider
                        collapsible
                        collapsed={collapsed}
                        onCollapse={setCollapsed}
                        breakpoint="lg"
                        theme={isDark ? "dark" : "light"}
                        width={260}
                        collapsedWidth={80}
                        trigger={null}
                        className="custom-sider"
                    >
                        <Sidebar
                            currentView={currentView}
                            setCurrentView={handleViewChange}
                            onClose={() => setSidebarOpen(false)}
                            isCollapsed={collapsed}
                            onCreateFolder={handleCreateFolder}
                            onCreateTag={handleCreateTag}
                            onEditFolder={handleEditFolder}
                            onEditTag={handleEditTag}
                            onDeleteFolder={handleDeleteFolder}
                            onDeleteTag={handleDeleteTag}
                        />
                    </Sider>
                )}

                <Layout>
                    {/* Desktop Header */}
                    {!isMobile && (
                        <Header className="custom-header">
                            <div className="header-left">
                                <button
                                    className="toggle-btn"
                                    onClick={() => setCollapsed(!collapsed)}
                                    title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                                >
                                    <Icon name="Menu" />
                                </button>
                                {!collapsed && <h1 className="app-title">Page Notes</h1>}
                            </div>
                            <div className="header-right">
                                <button
                                    className={`header-btn ${currentView === 'settings' ? 'active' : ''}`}
                                    onClick={() => handleViewChange('settings')}
                                    title="Settings"
                                >
                                    <Icon name="Settings" />
                                </button>
                                <button
                                    className={`header-btn ${currentView === 'account' ? 'active' : ''}`}
                                    onClick={() => handleViewChange('account')}
                                    title="Account"
                                >
                                    <Icon name="User" />
                                </button>
                            </div>
                        </Header>
                    )}

                    {/* Mobile Header */}
                    {isMobile && (
                        <MobileHeader
                            title={getHeaderTitle()}
                            onMenuClick={() => setSidebarOpen(true)}
                            onBack={handleBackFromEditor}
                            showBack={showEditor}
                            currentView={currentView}
                            setCurrentView={handleViewChange}
                        />
                    )}

                    {/* Main Content */}
                    <Content className={`main-content ${showEditor ? 'showing-editor' : ''}`}>
                        {renderMainContent()}
                    </Content>

                    {/* Mobile Bottom Navigation */}
                    {isMobile && <BottomNav currentView={currentView} setCurrentView={handleViewChange} />}
                </Layout>

                {/* Mobile Sidebar Overlay */}
                {isMobile && sidebarOpen && (
                    <div className="mobile-sidebar-overlay" onClick={() => setSidebarOpen(false)}>
                        <div className="mobile-sidebar" onClick={e => e.stopPropagation()}>
                            <Sidebar
                                currentView={currentView}
                                setCurrentView={handleViewChange}
                                onClose={() => setSidebarOpen(false)}
                                isCollapsed={false}
                                onCreateFolder={handleCreateFolder}
                                onCreateTag={handleCreateTag}
                                onEditFolder={handleEditFolder}
                                onEditTag={handleEditTag}
                                onDeleteFolder={handleDeleteFolder}
                                onDeleteTag={handleDeleteTag}
                            />
                        </div>
                    </div>
                )}

                {/* Share Modal */}
                <ShareModal
                    isOpen={shareModalState.isOpen}
                    note={shareModalState.note}
                    onClose={closeShareModal}
                />

                {/* Folder Modal */}
                <FolderModal
                    isOpen={folderModalState.isOpen}
                    folder={folderModalState.folder}
                    onClose={closeFolderModal}
                />

                {/* Tag Modal */}
                <TagModal
                    isOpen={tagModalState.isOpen}
                    tag={tagModalState.tag}
                    onClose={closeTagModal}
                />

                {/* Create Note Modal */}
                <CreateNoteModal
                    isOpen={createNoteModalOpen}
                    onClose={closeCreateNoteModal}
                    onNoteCreated={handleNoteCreated}
                />
            </Layout>
    );
}
